import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/footer.css';
import React, { Component } from 'react';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import * as actions from '../actions';

import ScrollToTopOnRouteChange from './ScrollToTopOnRouteChange';

import Header from './header/Main';

import Home from './home/Main';

import Login from './login/Main';
import Logout from './Logout';

import Dashboard from './Dashboard';
import DashboardHistory from './dashboard/DashboardHistory';
import DashboardSettings from './dashboard/DashboardSettings';
import NoPermissions from './NoPermissions';

import Contact from './contact/Main';
import ContactConfirmation from './contact/ContactConfirmation';

import AdminConsoleMain from './admin/console/Main';
import UserManagementPage from './admin/console/UserManagementPage';
import ModelManagement from './admin/console/ModelManagement';

import TenantList from './admin/tenants/TenantList';
import TenantCreate from './admin/tenants/TenantCreate';
import TenantDetail from './admin/tenants/TenantDetail';
import TenantTokens from './admin/tenants/TenantTokens';
import TenantUsers from './admin/tenants/TenantUsers';
import ImpersonationPanel from './admin/tenants/ImpersonationPanel';

import ArticleToMediaMain from './tools/article-to-media/Main';
import ResultsPage from './tools/article-to-media/components/ResultsPage';

import AdminToolsMain from './admin/tools/article-to-media/Main';
import AccessCodeManagement from './admin/tools/article-to-media/AccessCodeManagement';
import ToolSettings from './admin/tools/article-to-media/ToolSettings';
import PromptsManagement from './admin/tools/article-to-media/PromptsManagement';
import ArticleManagement from './admin/tools/article-to-media/ArticleManagement';
import DomainBlocklist from './admin/tools/article-to-media/DomainBlocklist';
import DomainAllowlist from './admin/tools/article-to-media/DomainAllowlist';
import ToolAnalytics from './admin/tools/article-to-media/ToolAnalytics';
import UrlProcessingLog from './admin/tools/article-to-media/UrlProcessingLog';
import DeleteReview from './admin/tools/article-to-media/DeleteReview';
import SpendReport from './admin/tools/article-to-media/SpendReport';
import FinancesReport from './admin/tools/article-to-media/FinancesReport';
import PendingDomainRequests from './admin/tools/article-to-media/PendingDomainRequests';
import ModelCatalogBrowser from './admin/tools/article-to-media/ModelCatalogBrowser';
import ErrorLogViewer from './admin/tools/article-to-media/ErrorLogViewer';

import TermsOfService from './legal/TermsOfService';
import PrivacyPolicy from './legal/PrivacyPolicy';
import NotFound from './common/NotFound';
import CookieConsent from './common/CookieConsent';
import ProfileOnboarding from './common/ProfileOnboarding';

import Footer from './Footer';
import SalesFooterCTA from './tools/article-to-media/components/SalesFooterCTA';

// Helper component to conditionally render header and footer
const ConditionalLayout = ({ children }) => {
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');
    const isFreeRoute = location.pathname.startsWith('/free');
    const isAppRoute = location.pathname.startsWith('/app');
    const isDashboardRoute = location.pathname.startsWith('/dashboard');

    // Hide sales CTA for authenticated users (admin, dashboard, app routes)
    // but show it on free tier and public pages
    const hideSalesCTA = isAdminRoute || isDashboardRoute || isAppRoute;

    // Hide "Try it Free" button on pages where user is already using the tool
    const hideTryItFree = isFreeRoute || isAppRoute;

    return (
        <>
            <Header />
            <div className="container-fluid px-0" style={{
                background: '#fafafa',
                color:"#1a1a2e",
                position:"relative",
                width:"100%",
                margin: "0",
                padding: "0",
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div className="px-0" style={{ flex: 1, minHeight: '1200px' }}>
                    {children}
                </div>
                <div className="container-fluid px-0" style={{ overflowX: 'hidden', marginTop: 'auto' }} >
                    {!hideSalesCTA && <SalesFooterCTA hideTryItFree={hideTryItFree} />}
                    <Footer />
                </div>
            </div>
        </>
    );
};

class App extends Component {

    componentDidMount() {
        this.props.fetchUser();
    }

    componentDidUpdate() {

    }

    render() {
        return (
            <>
                <BrowserRouter>
                    <ScrollToTopOnRouteChange />
                    <ConditionalLayout>
                        <Switch>
                            {/* Public Routes - Homepage is the article tool */}
                            <Route exact path="/" component={Home} />
                            <Route exact path="/login" component={Login} />
                            <Route exact path="/contact" component={Contact} />
                            <Route exact path="/contact/confirmation" component={ContactConfirmation} />

                            {/* Article Tool - Public access at /free */}
                            <Route exact path="/free" component={ArticleToMediaMain} />
                            <Route exact path="/free/:slug" component={ResultsPage} />

                            {/* Article Tool - Tenant access at /app */}
                            <Route exact path="/app/article-to-media" component={ArticleToMediaMain} />
                            <Route exact path="/app/article-to-media/:slug" component={ResultsPage} />

                            {/* Protected Routes */}
                            <Route exact path="/logout" component={Logout} />
                            <Route exact path="/dashboard" component={Dashboard} />
                            <Route exact path="/dashboard/history" component={DashboardHistory} />
                            <Route exact path="/dashboard/settings" component={DashboardSettings} />
                            <Route exact path="/no-permissions" component={NoPermissions} />

                            {/* Platform Admin Routes */}
                            <Route exact path="/admin/console" component={AdminConsoleMain} />
                            <Route exact path="/admin/platform/users" component={UserManagementPage} />
                            <Route exact path="/admin/platform/models" component={ModelManagement} />
                            {/* Legacy routes - redirect to new paths */}
                            <Route exact path="/admin/console/users" component={UserManagementPage} />
                            <Route exact path="/admin/console/models" component={ModelManagement} />

                            {/* Tenant Management Routes */}
                            <Route exact path="/admin/tenants" component={TenantList} />
                            <Route exact path="/admin/tenants/new" component={TenantCreate} />
                            <Route exact path="/admin/tenants/:id" component={TenantDetail} />
                            <Route exact path="/admin/tenants/:id/tokens" component={TenantTokens} />
                            <Route exact path="/admin/tenants/:id/users" component={TenantUsers} />
                            <Route exact path="/admin/impersonate" component={ImpersonationPanel} />

                            {/* Admin Tools Routes */}
                            <Route exact path="/admin/tools" component={AdminToolsMain} />
                            <Route exact path="/admin/tools/access-codes" component={AccessCodeManagement} />
                            <Route exact path="/admin/tools/settings" component={ToolSettings} />
                            <Route exact path="/admin/tools/prompts" component={PromptsManagement} />
                            <Route exact path="/admin/platform/articles" component={ArticleManagement} />
                            <Route exact path="/admin/tools/allowlist" component={DomainAllowlist} />
                            <Route exact path="/admin/tools/blocklist" component={DomainBlocklist} />
                            <Route exact path="/admin/tools/analytics" component={ToolAnalytics} />
                            <Route exact path="/admin/tools/url-log" component={UrlProcessingLog} />
                            <Route exact path="/admin/tools/delete-review" component={DeleteReview} />
                            <Route exact path="/admin/tools/spend-report" component={SpendReport} />
                            <Route exact path="/admin/tools/finances" component={FinancesReport} />
                            <Route exact path="/admin/tools/pending-domains" component={PendingDomainRequests} />
                            <Route exact path="/admin/tools/model-catalog" component={ModelCatalogBrowser} />
                            <Route exact path="/admin/tools/error-log" component={ErrorLogViewer} />

                            {/* Legal Routes */}
                            <Route exact path="/terms-of-service" component={TermsOfService} />
                            <Route exact path="/privacy-policy" component={PrivacyPolicy} />

                            {/* 404 Fallback - must be last */}
                            <Route component={NotFound} />
                        </Switch>
                    </ConditionalLayout>
                </BrowserRouter>
                <CookieConsent />
                <ProfileOnboarding />
            </>
        );
    }
};

function mapStateToProps({ auth }) {
    return { auth };
}

export default connect(mapStateToProps, actions)(App);
